'use client';

import Link from 'next/link';

export default function PrivacyPolicyPage() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        .policy-root {
          font-family: 'DM Sans', sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1a2e;
        }
        .policy-nav {
          background: #0d1b2a;
          padding: 0 32px;
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .policy-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .policy-logo-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, #00b4d8, #0077b6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .policy-logo-text {
          font-size: 20px;
          font-weight: 700;
          color: #ffffff;
        }
        .policy-logo-text span { color: #00b4d8; }
        .policy-back {
          color: #00b4d8;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }
        .policy-back:hover { opacity: 0.8; }
        .policy-hero {
          background: linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 100%);
          padding: 60px 32px;
          text-align: center;
        }
        .policy-hero h1 {
          font-size: 40px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 12px;
        }
        .policy-hero p {
          color: #8b9ab0;
          font-size: 15px;
          margin: 0;
        }
        .policy-content {
          max-width: 860px;
          margin: 0 auto;
          padding: 60px 32px;
        }
        .policy-card {
          background: #fff;
          border-radius: 16px;
          padding: 48px;
          border: 1px solid #e8ecf0;
          box-shadow: 0 4px 24px rgba(0,0,0,0.04);
        }
        .policy-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 32px 0 12px;
          padding-top: 32px;
          border-top: 1px solid #f0f4f8;
        }
        .policy-card h2:first-child {
          margin-top: 0;
          padding-top: 0;
          border-top: none;
        }
        .policy-card p, .policy-card li {
          font-size: 15px;
          line-height: 1.8;
          color: #4b5563;
          margin: 0 0 12px;
        }
        .policy-card ul {
          padding-left: 20px;
          margin: 0 0 16px;
        }
        .policy-card li { margin-bottom: 8px; }
        .policy-updated {
          display: inline-block;
          background: rgba(0,180,216,0.1);
          border: 1px solid rgba(0,180,216,0.3);
          color: #00b4d8;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 99px;
          margin-bottom: 32px;
        }
        .policy-footer {
          background: #0d1b2a;
          padding: 24px 32px;
          text-align: center;
          color: #8b9ab0;
          font-size: 13px;
        }
        .policy-footer a { color: #00b4d8; text-decoration: none; }
        .policy-footer a:hover { text-decoration: underline; }
      `}</style>

      <div className="policy-root">
        <nav className="policy-nav">
          <Link href="/" className="policy-logo">
            <div className="policy-logo-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" fill="#fff" />
                <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <span className="policy-logo-text">Cam<span>Smart</span></span>
          </Link>
          <Link href="/" className="policy-back">
            ← Back to Store
          </Link>
        </nav>

        <div className="policy-hero">
          <h1>Privacy Policy</h1>
          <p>How we collect, use, and protect your personal information</p>
        </div>

        <div className="policy-content">
          <div className="policy-card">
            <span className="policy-updated">Last Updated: May 9, 2026</span>

            <h2>1. Introduction</h2>
            <p>CamSmart ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase. Please read this policy carefully. If you disagree with its terms, please discontinue use of our site.</p>

            <h2>2. Information We Collect</h2>
            <p>We may collect the following types of information:</p>
            <ul>
              <li><strong>Personal Identification Information:</strong> Name, email address, phone number, billing and shipping address.</li>
              <li><strong>Payment Information:</strong> Credit/debit card details processed securely through our payment processor. We do not store full card numbers.</li>
              <li><strong>Device & Usage Data:</strong> IP address, browser type, operating system, pages visited, time spent on pages, and referring URLs.</li>
              <li><strong>Cookies & Tracking Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to enhance your experience and serve relevant advertisements.</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process and fulfill your orders</li>
              <li>Send order confirmations, shipping updates, and customer support communications</li>
              <li>Improve our website, products, and services</li>
              <li>Send promotional emails and offers (you may opt out at any time)</li>
              <li>Comply with legal obligations</li>
              <li>Detect and prevent fraud</li>
              <li>Serve personalized advertisements through Google Ads and other advertising networks</li>
            </ul>

            <h2>4. Google Ads & Advertising</h2>
            <p>We use Google Ads to display advertisements. Google uses cookies to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" style={{color:'#00b4d8'}}>Google Ads Settings</a>. We also use Google Analytics to analyze website traffic.</p>
            <p>Third-party vendors, including Google, use cookies to serve ads based on your past visits to our site. These cookies allow Google and its partners to serve ads to you based on your visit to our site and/or other sites on the Internet.</p>

            <h2>5. Cookies</h2>
            <p>We use cookies to:</p>
            <ul>
              <li>Remember your cart and preferences</li>
              <li>Analyze site traffic and usage patterns</li>
              <li>Deliver targeted advertisements</li>
              <li>Improve site functionality</li>
            </ul>
            <p>You can control cookies through your browser settings. Disabling cookies may affect certain features of our website.</p>

            <h2>6. Sharing Your Information</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Payment processors, shipping carriers, email service providers who assist in our operations</li>
              <li><strong>Advertising Partners:</strong> Google Ads and analytics providers (anonymized/aggregated data)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>

            <h2>7. Data Security</h2>
            <p>We implement industry-standard security measures including SSL encryption, secure servers, and regular security audits to protect your personal information. However, no method of transmission over the Internet is 100% secure.</p>

            <h2>8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your personal data</li>
              <li>Opt out of marketing communications at any time</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>

            <h2>9. Children's Privacy</h2>
            <p>Our website is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.</p>

            <h2>10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated date. Your continued use of our website after changes constitutes acceptance of the updated policy.</p>

            <h2>11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p><strong>CamSmart</strong><br />Email: privacy@camsmart.com<br />Phone: 1-800-CAM-SMART</p>
          </div>
        </div>

        <footer className="policy-footer">
          <p>© 2026 CamSmart. All rights reserved. &nbsp;|&nbsp; <Link href="/privacy-policy">Privacy Policy</Link> &nbsp;|&nbsp; <Link href="/terms-of-service">Terms of Service</Link> &nbsp;|&nbsp; <Link href="/disclaimer">Disclaimer</Link></p>
        </footer>
      </div>
    </>
  );
}
